/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Type,
    ViewChild,
} from '@angular/core';

import {
    ALBServicesConnectivityStatus,
    ALBServicesRegistrationStatus,
    IALBServicesAssetDetails,
    IALBServicesConfig,
    ICloudLicenseSubscriptionRequest,
    IQuotaConfig,
    LicenseTierType,
} from 'generated-types';

import {
    CportalService,
    IRegisterControllerPayLoad,
    PortalInfo,
} from 'ajs/modules/cportal';

import {
    AlbServicesSiteCollection,
    LicensePoolCollection,
    TAlbServicesSiteCollection,
    TLicensePoolCollection,
} from 'ng/modules/collections/factories';

import {
    SystemSettingsModalComponent,
} from 'ng/modules/system/components/system-settings-modal/system-settings-modal.component';

import { ClrFormLayout } from '@clr/angular';
import { ClusterItem } from 'ng/modules/items/factories';
import { createDropdownOption } from 'ng/shared/utils';
import { DefaultValues } from 'ajs/modules/core/services';
import { FullModalService } from 'ng/modules/core/services';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown';
import { ILicenseTiersUsageData, ILicensingStatusResponse } from 'ng/modules/licensing/licensing.types';
import { IPromise } from 'angular';
import { L10nService } from '@vmw/ngx-vip';
import { LicensingService } from 'ajs/modules/licensing';
import { NgForm } from '@angular/forms';
import { portalStatus } from 'ajs/modules/cportal/constants';
import { Subject, takeUntil } from 'rxjs';
import { SystemConfig } from 'ajs/modules/system';
import * as globalL10n from 'global-l10n';

import './license-tier-switch-modal.component.less';
import { AVI_LOAD_BALANCER_LICENSING_GUIDE } from '../../constants';
import { LicensingPageStore } from '../licensing-page.store';
import { ProxyConfigModalComponent } from './proxy-config-modal';
import * as l10n from './license-tier-switch-modal.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const ObjectType = 'SaasLicensingInfo';

type TPortalInfo = typeof PortalInfo;
type TCluster = typeof ClusterItem;

/**
 * @description
 *
 *     License tier switch modal.
 *
 * @author Zhiqian Liu, Deepak Chaudhary, Shubam Bindal
 */
@Component({
    selector: 'license-tier-switch-modal',
    templateUrl: './license-tier-switch-modal.component.html',
})
export class LicenseTierSwitchModalComponent implements OnInit, OnDestroy {
    /**
     * SystemConfig editable object to change licensing tier on.
     */
    @Input()
    public editable: SystemConfig;

    /**
     * Usage data of license tiers, containing used and total service cores of each tier type.
     */
    @Input()
    public licenseTiersUsageData!: ILicenseTiersUsageData;

    /**
     * Fire when save succeeds.
     */
    @Output()
    public onSaveSuccess = new EventEmitter<void>();

    /**
     * Fire when user wants to open cloud services modal after save.
     */
    @Output()
    public onOpenCloudServicesModal = new EventEmitter<void>();

    /**
     * Template ref form element.
     */
    @ViewChild('form')
    public form!: NgForm;

    /**
     * Holds the customer portal information.
     */
    public portalInfo: IALBServicesConfig;

    /**
     * Hold the PortalInfo instance used for proxy configuration.
     */
    public portalInfoItem: PortalInfo;

    /**
     * Holds the value of ENTERPRISE_WITH_CLOUD_SERVICES license tier.
     */
    public readonly enterpriseWithCloudServicesTier = LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES;

    /**
     * List of all LicenseTierType enum values.
     */
    public readonly licenseTierTypeList = [
        LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES,
        LicenseTierType.ENTERPRISE,
    ];

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    public readonly aviLoadBalancerLicensingKbUrl = AVI_LOAD_BALANCER_LICENSING_GUIDE;

    /**
     * Layout for modal.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    /**
     * If true, we open cloud modal right after save.
     */
    public openCloudModal = false;

    /**
     * Object type.
     */
    public objectType!: string;

    /**
     * Holds error message if any.
     */
    public error!: string;

    /**
     * Selected default contact.
     */
    public defaultContact!: string;

    /**
     * Selected site Id.
     */
    public selectedSiteId!: string;

    /**
     * Selected pool Id.
     */
    public selectedPoolId!: string;

    /**
     * User Selector dropdown options.
     */
    public users: IAviDropdownOption[] = [];

    /**
     * Get keys from global source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * License Quota config of system settings.
     */
    public licenseQuotaConfig: IQuotaConfig;

    /**
     * Cluster item instance.
     */
    public clusterItem: ClusterItem = null;

    public readonly albServicesSiteCollection: AlbServicesSiteCollection;

    public readonly licensePoolCollection: LicensePoolCollection;

    /**
     * Currently in-use license tier type.
     */
    public tierInUse: LicenseTierType;

    public infoAlertCleared = false;

    /**
     * Hold component busy state.
     */
    private busy = false;

    private connectivityStatus?: ALBServicesConnectivityStatus;

    private registrationStatus?: ALBServicesRegistrationStatus;

    private licenseStatus?: ILicensingStatusResponse;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    constructor(
        @Inject('PortalInfo')
        private readonly PortalInfo: TPortalInfo,
        @Inject(ClusterItem)
        private readonly ClusterClass: TCluster,
        @Inject('systemConfigService')
        private readonly systemConfigService: SystemConfig,
        @Inject(AlbServicesSiteCollection)
        AlbServicesSiteCollectionClass: TAlbServicesSiteCollection,
        @Inject(LicensePoolCollection)
        LicensePoolCollectionClass: TLicensePoolCollection,
        private l10nService: L10nService,
        private readonly cPortalService: CportalService,
        private readonly defaultValues: DefaultValues,
        private readonly licensingPageStore: LicensingPageStore,
        private readonly licensingService: LicensingService,
        private readonly fullModalService: FullModalService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.albServicesSiteCollection = new AlbServicesSiteCollectionClass();
        this.licensePoolCollection = new LicensePoolCollectionClass();
        this.clusterItem = new this.ClusterClass();
        this.portalInfoItem = new this.PortalInfo();
        this.licenseQuotaConfig = this.systemConfigService.getLicenseQuota();
    }

    /**
     * Getter for the selected license tier.
     */
    public get selectedTier(): LicenseTierType {
        return this.editable.defaultLicenseTier;
    }

    /**
     * Return true if the selected license tier is ENTERPRISE_WITH_CLOUD_SERVICES
     */
    public get isCloudServiceTierSelected(): boolean {
        return this.selectedTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES;
    }

    public get saveLabel(): string {
        return this.isCloudServiceTierSelected && this.openCloudModal ?
            this.l10nService.getMessage(l10nKeys.saveAndContinue) :
            this.l10nService.getMessage(globalL10nKeys.saveLabel);
    }

    /**
     * Return true if on-prem licensing is enabled.
     */
    public get isOnPremLicensingEnabled(): boolean {
        return this.licenseStatus?.cls_status?.enabled;
    }

    /**
     * Return true if licensing is enabled (for Cloud licensing).
     */
    public get isCloudLicensingEnabled(): boolean {
        return this.licenseStatus?.saas_status?.enabled;
    }

    /**
     * Return true if licensing is connected (for Cloud licensing).
     */
    public get isCloudLicensingConnected(): boolean {
        return this.licenseStatus?.saas_status?.connected;
    }

    /**
     * Return true if "Configure Cloud Services" checkbox should be shown.
     * Visible when tier is ENTERPRISE_WITH_CLOUD_SERVICES and
     * - (DEREGISTERED and CONNECTED) OR
     * - (REGISTERED and CONNECTED and cloud licensing not enabled)
     */
    public get showConfigureCloudServicesCheckbox(): boolean {
        if (this.selectedTier !== LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES || !this.isConnected) {
            return false;
        }

        return !this.isRegistered || !this.isCloudLicensingEnabled;
    }

    /**
     * Return true if controller is registered with cloud services.
     */
    public get isRegistered(): boolean {
        return this.registrationStatus === portalStatus.ALBSERVICES_REGISTERED;
    }

    /**
     * Return true if controller is connected to cloud services.
     */
    public get isConnected(): boolean {
        return this.connectivityStatus === portalStatus.ALBSERVICES_CONNECTED;
    }

    /**
     * Return true if Site, Cluster Name and Contact fields should be read-only.
     * Read-only when: registered and connected, OR cloud licensing is enabled.
     */
    public get isSiteContactClusterReadOnly(): boolean {
        return this.isRegistered && this.isConnected || this.isCloudLicensingEnabled;
    }

    /**
     * Decide if the alert to switch to cloud licensing should be shown.
     * The alert should be shown when:
     * 1. Current tier is ENTERPRISE (On-Prem) and licensing is enabled.
     * 2. Current tier is ENTERPRISE_WITH_CLOUD_SERVICES (Cloud) and licensing enabled.
     */
    public get showSwitchToCloudAlert(): boolean {
        if (this.selectedTier === LicenseTierType.ENTERPRISE) {
            return this.isOnPremLicensingEnabled;
        }

        if (this.selectedTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES) {
            return this.isCloudLicensingEnabled;
        }

        return false;
    }

    /**
     * Used to track if the form is modified.
     * If True, enable Save button.
     */
    public get isFormModified(): boolean {
        const tierChanged = this.tierInUse !== this.selectedTier;
        const cloudServiceFormDirty = this.tierInUse === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES &&
            (this.form?.dirty ?? false);

        return tierChanged || cloudServiceFormDirty;
    }

    /** @override */
    public ngOnInit(): void {
        this.objectType = ObjectType;
        this.tierInUse = this.editable.getConfig().default_license_tier as LicenseTierType;
        this.busy = true;
        this.generateProxyConfig();

        if (this.tierInUse === LicenseTierType.ENTERPRISE) {
            this.licensingPageStore.loadConnectivityStatus();
        }

        this.cPortalService.getPortalInfo().then((portalData: IALBServicesConfig) => {
            this.portalInfo = portalData;
            this.busy = false;

            if (!this.portalInfo.saas_licensing_config) {
                this.portalInfo.saas_licensing_config = this.defaultValues
                    .getDefaultItemConfig('albservicesconfig.saas_licensing_config');
            }
        });

        this.licensingPageStore.licenseStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((licenseStatus: ILicensingStatusResponse) => {
                this.licenseStatus = licenseStatus;
            });

        this.licensingPageStore.connectivityStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((connectivityStatus: ALBServicesConnectivityStatus) => {
                this.connectivityStatus = connectivityStatus;
            });

        this.licensingPageStore.registrationStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((registrationStatus: ALBServicesRegistrationStatus) => {
                this.registrationStatus = registrationStatus;
            });

        this.clusterItem.load();
        this.licensingPageStore.assetDetails$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((assetDetails: IALBServicesAssetDetails) => {
                if (assetDetails?.site?.site_id && !this.selectedSiteId) {
                    this.albServicesSiteCollection.load().then(() => {
                        this.selectedSiteId = assetDetails.site.site_id;
                    });

                    this.licensePoolCollection.setParams({ site_id: assetDetails.site.site_id });

                    if (this.licenseStatus?.saas_status?.pool?.pool_id && !this.selectedPoolId) {
                        this.licensePoolCollection.load().then(() => {
                            this.selectedPoolId = this.licenseStatus.saas_status.pool.pool_id;
                        });
                    }
                }

                this.defaultContact = assetDetails?.email;
                this.users = assetDetails?.email ? [createDropdownOption(assetDetails?.email)] : [];
            });
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
        this.albServicesSiteCollection.destroy();
        this.licensePoolCollection.destroy();
        this.portalInfoItem.destroy();
        this.clusterItem.destroy();
    }

    /**
     * Initialize and load the portal configuration required for proxy setup.
     */
    public async generateProxyConfig(): Promise<void> {
        if (!this.portalInfoItem) {
            this.portalInfoItem = new this.PortalInfo();
        }

        await this.portalInfoItem.load();
    }

    /**
     * Check busy state, decided by editable busy state and component busy state.
     */
    public isBusy(): boolean {
        return this.editable.isBusy() || this.busy;
    }

    /**
     * Called when the user wants to cancel or to close the modal.
     */
    public dismiss(): void {
        this.editable.closeLicenseTierModal();
        this.busy = false;
    }

    /**
     * trackBy function for tier selection cards.
     */
    public trackByTierType(index: number, tierType: LicenseTierType): LicenseTierType {
        return tierType;
    }

    /**
     * Get usage fraction string by tier type.
     */
    public getTierUsageFraction(tierType: LicenseTierType): string {
        if (!this.licenseTiersUsageData?.[tierType]) {
            return '0';
        }

        const isInUse = this.isTierInUse(tierType);
        const { used, total } = this.licenseTiersUsageData[tierType];

        // show the usage fraction for an active type
        if (isInUse) {
            return `${used}/${total}`;
        }

        // only show the total number of cores for an in-active type
        return `${total}`;
    }

    /**
     * Fired when site is changed.
     */
    public onSiteChange(selectedSiteId: string): void {
        this.licensePoolCollection.setParams({ site_id: selectedSiteId });
        this.selectedPoolId = '';
    }

    /**
     * Show the alert when tier is ENTERPRISE_WITH_CLOUD_SERVICES,
     * controller is disconnected, and cloud licensing is not enabled.
     */
    public showConnectNowAlert(tierType: LicenseTierType): boolean {
        return tierType === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES &&
            this.selectedTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES &&
            this.connectivityStatus === portalStatus.ALBSERVICES_DISCONNECTED &&
            !this.isCloudLicensingEnabled;
    }

    /**
     * Return true if the controller is connected to the cloud and registered with the cloud.
     */
    public isDisabled(): boolean {
        switch (this.selectedTier) {
            case LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES:
                return this.isConnected && this.isRegistered;
            case LicenseTierType.ENTERPRISE:
                return this.isOnPremLicensingEnabled;
            default:
                return false;
        }
    }

    /**
     * Decide if the cloud licensing settings should be shown.
     * Show when tier is ENTERPRISE_WITH_CLOUD_SERVICES and either:
     * - Controller is connected to the cloud, OR
     * - Cloud licensing is already enabled (saasStatus.enabled is true)
     */
    public showCloudLicensingSettings(tierType: LicenseTierType): boolean {
        return tierType === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES &&
            this.selectedTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES &&
            (this.connectivityStatus === portalStatus.ALBSERVICES_CONNECTED || this.isCloudLicensingEnabled);
    }

    /**
     * Decide if switching to a tier is disabled.
     * - ENTERPRISE tier disabled when: saasStatus.enabled is true
     * - ENTERPRISE_WITH_CLOUD_SERVICES tier disabled when: clsStatus.enabled is true
     */
    public isTierDisabled(tierType: LicenseTierType): boolean {
        return tierType === LicenseTierType.ENTERPRISE && this.isCloudLicensingEnabled ||
            tierType === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES && this.isOnPremLicensingEnabled;
    }

    /**
     * Set selected tier to the respect value when the selection card is clicked.
     */
    public selectTier(tierType: LicenseTierType): void {
        this.editable.defaultLicenseTier = tierType;
    }

    /**
     * Decide whether a tier is selected.
     */
    public isSelected(tierType: LicenseTierType): boolean {
        return tierType === this.selectedTier;
    }

    /**
     * Open cluster edit modal.
     */
    public openClusterEditModal(): void {
        this.clusterItem.edit().then(() => {
            setTimeout(() => {
                this.clusterItem.load();
            }, 1000);
        });
    }

    /**
     * Open proxy configuration modal.
     */
    public openProxyConfigModal(): void {
        this.fullModalService.addModal({
            component: ProxyConfigModalComponent as Type<Component>,
            componentProps: {
                portalInfoItem: this.portalInfoItem,
                onCancel: () => {
                    this.fullModalService.removeModalByComponent(ProxyConfigModalComponent as Type<Component>);
                },
                onSubmit: () => {
                    this.fullModalService.removeModalByComponent(ProxyConfigModalComponent as Type<Component>);
                    this.generateProxyConfig();
                },
            },
            getName: () => this.l10nService.getMessage(l10nKeys.proxyHeaderLabel),
        });
    }

    /**
     * Open system settings modal and scroll to license quota config section.
     */
    public async openSystemSettingsModal(): Promise<void> {
        await this.systemConfigService.edit(SystemSettingsModalComponent as Type<Component>, {
            scrollToSelector: 'license-quota-config',
        }).then(() => this.licenseQuotaConfig = this.systemConfigService.getLicenseQuota());
    }

    /**
     * Trigger saving for license tier type.
     * For ENTERPRISE_WITH_CLOUD_SERVICES tier:
     * 1. Save the default license tier type
     * 2. If not registered but connected: Register controller then subscribe to cloud licensing
     * 3. If already registered and connected: Only subscribe to cloud licensing
     */
    public saveLicenseTierChange = async(): Promise<void> => {
        this.busy = true;

        try {
            await this.editable.saveDefaultLicenseTierType();

            this.licensingPageStore.setSelectedLicensingTier(this.selectedTier);

            if (this.selectedTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES && this.isConnected) {
                if (!this.isRegistered) {
                    await this.registerAndSubscribeToCloudLicensing();
                } else {
                    await this.subscribeToCloudLicensing();
                }
            }

            this.onSaveSuccess.emit();

            this.dismiss();
        } catch (error) {
            const errorObj = error as { error?: string };

            this.error = errorObj?.error ||
                this.l10nService.getMessage(l10nKeys.defaultSaveTierChangeErrorMessage);
        } finally {
            this.busy = false;
            this.licensingPageStore.setTierChangeInProgress(false);
        }
    };

    /**
     * Authenticate user with the portal similar to connectAndRegisterController.
     */
    public handleConnectToCloudLicensing(): IPromise<string | void> {
        return this.connectAviCloud()
            .then(async() => {
                await this.licensingPageStore.loadConnectivityStatus();
                await this.licensingPageStore.loadLicenseStatus();

                const aviCloudConnected = this.selectedTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES &&
                    this.connectivityStatus === portalStatus.ALBSERVICES_CONNECTED &&
                    this.registrationStatus === portalStatus.ALBSERVICES_REGISTERED;

                if (!aviCloudConnected) {
                    this.error = this.l10nService.getMessage(l10nKeys.licensingConnectivityError);
                }
            })
            .catch(error => {
                const errorObj = error as { error?: string };

                this.error = errorObj?.error ||
                    this.l10nService.getMessage(l10nKeys.failedToConnectCloudLicensingError);
            })
            .finally(() => this.busy = false);
    }

    /**
     * Close the info alert if user click on close icon.
     */
    public closeInfoAlert(): void {
        this.infoAlertCleared = true;
    }

    /**
     * Function to authenticate user with Portal.
     */
    private connectAviCloud(): IPromise<void> {
        const aviCloudConnected = this.selectedTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES &&
            this.connectivityStatus === portalStatus.ALBSERVICES_CONNECTED &&
            this.registrationStatus === portalStatus.ALBSERVICES_REGISTERED;

        return aviCloudConnected ? Promise.resolve() : this.cPortalService.openLoginWindow();
    }

    /**
     * Register controller and subscribe to cloud licensing.
     * Step 1: Register controller using IRegisterControllerPayLoad
     * Step 2: Subscribe to cloud licensing using ICloudLicensingSubscriptionPayload
     */
    private async registerAndSubscribeToCloudLicensing(): Promise<void> {
        const registerPayload: IRegisterControllerPayLoad = this.getRegisterControllerPayload();

        await this.cPortalService.registerController(registerPayload);

        await this.subscribeToCloudLicensing();
    }

    /**
     * Subscribe to cloud licensing if pool is selected.
     * If openCloudModal is true and subscription succeeded,
     * emit event to open cloud services modal.
     */
    private async subscribeToCloudLicensing(): Promise<void> {
        if (this.selectedPoolId) {
            const subscribePayload: ICloudLicenseSubscriptionRequest = {
                pool_id: this.selectedPoolId,
            };

            await this.licensingService.subscribeCloudLicensing(subscribePayload)
                .then(() => {
                    if (this.openCloudModal) {
                        this.onOpenCloudServicesModal.emit();
                    }
                });
        }
    }

    /**
     * Get registration payload for controller registration.
     */
    private getRegisterControllerPayload(): IRegisterControllerPayLoad {
        return {
            name: this.clusterItem?.getName() ?? '',
            email: this.defaultContact,
            site_id: this.selectedSiteId,
            description: this.clusterItem?.getName() ?? '',
        };
    }

    /**
     * Decide if a tier type is the current one in use.
     */
    private isTierInUse(tierType: LicenseTierType): boolean {
        return tierType === this.tierInUse;
    }
}
